import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/shallow";

import { ProfileStep, ProfileStepRef } from "@/features/onboarding/components/ProfileStep";
import { WelcomeStep } from "@/features/onboarding/components/WelcomeStep";
import { CompletionStep } from "@/features/onboarding/components/CompletionStep";
import { EditToolbarStep } from "@/features/onboarding/components/EditToolbarStep";
import { ThemeStep } from "@/features/onboarding/components/ThemeStep";
import { EncryptionStep } from "@/features/onboarding/components/EncryptionStep";
import { hooks } from "@/services/api/hooks";
import { useBoundStore } from "@/app/store";
import { Stepper, StepperStep } from "@/shared/components/common/Stepper";
import { usePageLayout } from "@/app/hooks/usePageLayout";
import { usePreventScrollOnKeyboardOpen } from "@/shared/hooks/usePreventScrollOnKeyboardOpen";

import { supabase } from "@/services/supabase/client";

const STEP_IDS = {
  WELCOME: "welcome",
  PROFILE: "profile",
  THEME: "theme",
  TOOLBAR: "toolbar",
  ENCRYPTION: "encryption",
  READY: "ready",
} as const;

export function OnboardingRoute() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const { settings, updateSettings } = useBoundStore(
    useShallow((state) => ({ settings: state.settings, updateSettings: state.settings.updateSettings }))
  );
  const profileStepRef = useRef<ProfileStepRef>(null);
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const createProfile = hooks.profiles.useCreateProfile();
  const updateProfile = hooks.profiles.useUpdateProfile();
  const { data: profile, isLoading: isLoadingProfile } = hooks.profiles.useFetchProfile(userId);
  const profileCreated = useRef(false);
  const navigate = useNavigate();

  const ONBOARDING_STEPS: StepperStep[] = [
    {
      id: STEP_IDS.WELCOME,
      title: "Welcome to InkRows",
      description: "Let's get you started with organizing your thoughts",
      content: <WelcomeStep />,
    },
    {
      id: STEP_IDS.PROFILE,
      title: "Set Up Your Profile",
      description: "Customize your profile or continue with defaults",
      badge: "Optional",
      content: <ProfileStep ref={profileStepRef} />,
    },
    {
      id: STEP_IDS.THEME,
      title: "Choose Your Theme",
      description: "Select your preferred color theme",
      content: <ThemeStep />,
    },
    {
      id: STEP_IDS.TOOLBAR,
      title: "Customize Your Toolbar",
      description: "Set up your note editor toolbar (drag to reorder)",
      content: <EditToolbarStep />,
    },
    {
      id: STEP_IDS.ENCRYPTION,
      title: "Setup Encryption",
      description: "This step is optional but recommended for securing your data",
      badge: "Optional",
      content: null,
    },
    {
      id: STEP_IDS.READY,
      title: "You're All Set!",
      description: "Your account is ready to use",
      content: <CompletionStep />,
    },
  ] as const;

  // Helper to get step index by step ID
  const getStepIndex = (stepId: string) => ONBOARDING_STEPS.findIndex((step) => step.id === stepId);

  useEffect(() => {
    if (!profile && !isLoadingProfile && userId && !profileCreated.current) {
      profileCreated.current = true;
      (async () => {
        try {
          const {
            data: { user },
          } = await supabase.auth.getUser();

          const initialName =
            user?.user_metadata?.full_name ||
            user?.user_metadata?.name ||
            [user?.user_metadata?.given_name, user?.user_metadata?.family_name].filter(Boolean).join(" ").trim() ||
            null;

          await createProfile.mutateAsync({
            id: userId,
            name: initialName || null,
          });
        } catch (error) {
          console.error("Error creating initial profile:", error);
          await createProfile.mutateAsync({ id: userId });
        }
      })();
    }
  }, [profile, isLoadingProfile, userId, createProfile]);

  const handleNext = async () => {
    const currentStepId = ONBOARDING_STEPS[currentStep]?.id;

    // Validate profile step before proceeding
    if (currentStepId === STEP_IDS.PROFILE) {
      if (!profileStepRef.current?.validate()) {
        return;
      }

      if (userId) {
        try {
          const { username, bio } = profileStepRef.current.getData();

          updateProfile.mutateAsync({ userId, profile: { name: username, bio: bio || null } });
        } catch (error) {
          console.error("Error saving profile:", error);
          return;
        }
      }
    }

    // Handle Get Started button (final step)
    if (currentStep === ONBOARDING_STEPS.length - 1) {
      if (userId) {
        await updateSettings(userId, { app: { ...settings.app, onboardingDone: true } });
        navigate("/home", { replace: true });
      }
      return;
    }

    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const steps = ONBOARDING_STEPS.map((step) => {
    if (step.id === STEP_IDS.ENCRYPTION) {
      return { ...step, content: <EncryptionStep onEncryptionStateChange={setIsEncrypting} /> };
    }
    return step;
  });
  const encryptionStepIndex = getStepIndex(STEP_IDS.ENCRYPTION);
  const pageLayoutConfig = useMemo(
    () => ({
      className: "p-4 pt-2",
      navbar: { hideLogo: true, hideBack: true, title: "InkRows" },
      resizeViewportOnKeyboardOpen: true,
    }),
    []
  );
  usePageLayout(pageLayoutConfig);
  usePreventScrollOnKeyboardOpen();

  return (
    <div className="mx-auto flex h-full max-w-sm justify-center sm:items-center">
      <Stepper
        classNames={{ content: "px-4", navigationButtons: "p-4" }}
        isDisabled={currentStep === encryptionStepIndex && isEncrypting}
        steps={steps}
        currentStep={currentStep}
        onNext={handleNext}
        onPrevious={handlePrevious}
        nextButtonLabel={currentStep === ONBOARDING_STEPS.length - 1 ? "Get Started" : "Next"}
      />
    </div>
  );
}
